import { cn } from "@/lib/utils"
import { Carousel } from "@/components/ui/carousel"
import { ResultCard } from "@/components/ui/result-card"
import { results } from "./content"
import { toneBg, type SectionTone } from "./section-tone"
import merchantLogo from "@/assets/client-logos/merchant-automotive.png"
import elecDirectLogo from "@/assets/client-logos/elec-direct.png"
import aceCanineLogo from "@/assets/client-logos/ace-canine.png"
import epsilonLogo from "@/assets/client-logos/epsilon.png"
import hitechLogo from "@/assets/client-logos/hitech.png"
import nexthermalLogo from "@/assets/client-logos/nexthermal.png"
import bissellLogo from "@/assets/client-logos/bissell.png"
import climbVertexLogo from "@/assets/client-logos/climb-vertex.png"
import bartonLogo from "@/assets/client-logos/barton.png"
import golfLogo from "@/assets/client-logos/golf-international.png"
import schorrLogo from "@/assets/client-logos/schorr-law.svg"

// Client logo keyed by the `client` string in content.ts. Cards without an
// entry keep the default result-card icon. The UK client-voice cards (NOVA,
// Warm Homes, SolarHero) live in the Testimonials section; the video cards
// live in the Case studies section.
const CLIENT_LOGOS: Record<string, string> = {
  "Merchant Automotive": merchantLogo,
  "Schorr Law": schorrLogo,
  "Elec Direct": elecDirectLogo,
  "Ace Canine": aceCanineLogo,
  "Epsilon Tech": epsilonLogo,
  "HiTech Wireless": hitechLogo,
  "Nexthermal": nexthermalLogo,
  "Bissell Woodworking": bissellLogo,
  "Climb Vertex": climbVertexLogo,
  "Barton Door": bartonLogo,
  "Golf International": golfLogo,
}

// Results section: the numbers-only result cards, two per row. Placeholder
// "coming soon" cards render only in development so a blank card can't ship.
export function Results({ tone = "page" }: { tone?: SectionTone }) {
  const shownResults = results.filter(
    (result) => import.meta.env.DEV || !result.placeholder,
  )

  return (
    <section
      id="results"
      data-section="results"
      // overflow-x-clip: the carousel's prev arrow hangs in the left gutter, so
      // clip any horizontal bleed rather than let it add page scroll.
      className={cn("scroll-mt-24 overflow-x-clip border-b border-hairline", toneBg[tone])}
    >
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
        <span className="section-eyebrow">Results</span>
        <h2 className="section-title mt-3 max-w-2xl">
          Real accounts, real numbers.
        </h2>

        <Carousel
          alignStart
          autoAdvanceMs={0}
          ariaLabel="Client results"
          className="mt-10"
        >
          {shownResults.map((result) => (
            <div key={result.client} className="w-72 shrink-0 snap-start sm:w-80">
              <ResultCard
                client={result.client}
                context={result.context}
                tag={result.tag}
                figure={result.figure}
                figureLabel={result.figureLabel}
                detail={result.detail}
                logo={CLIENT_LOGOS[result.client]}
                className="h-full"
              />
            </div>
          ))}
        </Carousel>
      </div>
    </section>
  )
}
